<template>
  <div class="dataTransferDiv">
    <project-toolbar
      :selectMonth="true"
      :selectProject="true"
      :project="accountProject"
    >
    </project-toolbar>
    <md-tabs
      v-if="tabs.length > 0"
      :md-active-tab="activeTab"
      @md-changed="changeTab"
    >
      <md-tab
        v-for="tab in tabs"
        :key="tab.name"
        :id="tab.name"
        :md-label="tab.name"
      />
    </md-tabs>
    <Split direction="vertical" style="height: 800px">
      <SplitArea :size="60">
        <div
          class="md-layout network-chart"
          v-if="transferGraphData.length !== 0"
        >
          <div class="md-layout-item" style="height: 100%">
            <network
              ref="charts"
              style="height: 100%"
              :nodes="transferGraphData.nodes"
              :edges="transferGraphData.edges"
              :options="transferGraphData.options"
            ></network>
          </div>
        </div>
      </SplitArea>
      <SplitArea :size="40">
        <md-table
          v-if="transferTableData.length > 0"
          class="region-table"
          v-model="transferTableData"
          :md-sort="sort.sort"
          :md-sort-order="sort.sortOrder"
          style="height: 100%"
          md-stripe
          md-thin
        >
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell
              class="fromWidthRate"
              :md-label="L('from')"
              md-sort-by="from"
              >{{ item.from }}</md-table-cell
            >
            <md-table-cell :md-label="L('to')" md-sort-by="to">{{
              item.to
            }}</md-table-cell>
            <md-table-cell :md-label="L('traffic')" md-sort-by="usage">{{
              item.usage | formatBytes
            }}</md-table-cell>
            <md-table-cell :md-label="L('cost')" md-sort-by="cost" md-numeric>{{
              item.cost | formatCurrency(displayCurrency)
            }}</md-table-cell>
            <md-table-cell
              :md-label="C('finalCost')"
              md-sort-by="finalCost"
              md-numeric
              >{{
                item.finalCost | formatCurrency(displayCurrency)
              }}</md-table-cell
            >
          </md-table-row>
        </md-table>
      </SplitArea>
    </Split>
  </div>
</template>

<script>
import { Network } from 'vue2vis'
import 'vue2vis/dist/vue2vis.css'
import { Split, SplitArea } from 'vue-split-panel'
import { mapState } from 'vuex'
import api from '@/api'
import { formatBytes } from '@/lib/textutil'
import { getExchangeRate } from '@/lib/exchangeRate'
import { getProject } from '@/lib/aggutil'
import { convertCosts } from '@/lib/utils'
import ProjectToolbar from './ProjectToolbar'

export default {
  components: {
    ProjectToolbar,
    Network,
    Split,
    SplitArea,
  },

  data: () => ({
    activeTab: null,
    project: null,
    accountProject: {
      all: false,
      list: [],
    },
    allowAll: false,
    sort: {
      sort: 'cost',
      sortOrder: 'desc',
    },
    dataTransfers: [],
  }),

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      curProject: (state) => state.curProject,
      curMonth: (state) => state.curMonth,
      exchangeRateList: (state) => state.exchangeRateList,
      userInfo: (state) => state.userInfo,
      displayCurrency: (state) => state.displayCurrency,
    }),

    transferGraphData() {
      return this.tabs.length ? this.setTransferGraphData(this.tabs) : []
    },

    transferTableData: {
      get() {
        return this.tabs.length
          ? this.setTransferTableData(this.tabs, this.displayCurrency)
          : []
      },
      set() {},
    },

    tabs() {
      return this.dataTransfers.map((tab) => ({
        name: tab.kind,
        chart: this.graphFilterData(tab.kind, tab.data),
        table: this.setTableData(tab.data),
        sort: 'usage',
        sortOrder: 'desc',
      }))
    },
  },

  watch: {
    curAccount() {
      this.activeTab = null
      this.project = null
      this.load()
    },

    curMonth() {
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      if (!this.curAccount.id) {
        return
      }
      await getExchangeRate(this.curMonth)
      const transferData = await api.aggregate.dataTransfer(
        this.curAccount.id,
        this.curMonth
      )
      this.accountProject = getProject(
        transferData,
        this.userInfo,
        this.curAccount
      )
      if (!this.curProject.id) {
        const curProject = { id: null, name: '' }
        if (!this.accountProject.all && this.accountProject.list.length > 0) {
          curProject.id = this.accountProject.list[0].id
          curProject.name = this.accountProject.list[0].name
        } else if (this.accountProject.all) {
          curProject.id = '__total'
        }
        this.$store.commit('curProject', curProject)
      }

      this.dataTransfers = transferData.filter(
        (tab) => !tab.kind.startsWith('Google')
      )
      if (!this.activeTab && this.dataTransfers.length) {
        this.activeTab = this.dataTransfers[0].kind
      }
    },

    projectId() {
      return this.curProject.id
    },

    loadProject(project) {
      this.project = project
    },

    changeTab(type) {
      this.activeTab = type
    },

    graphFilterData(kind, data) {
      return data.reduce((arr, items) => {
        arr.push({
          projectId: items.project_id,
          projectName: items.project_name,
          transferData: this.setTransferLineData(items),
          kind,
        })
        return arr
      }, [])
    },

    setTableData(data) {
      return data.reduce((arr, items) => {
        const costData = this.setCostArray(items.data)
        arr.push({
          projectId: items.project_id,
          projectName: items.project_name,
          data: costData.sort((a, b) => b.cost - a.cost),
        })
        return arr
      }, [])
    },

    getExchangeRate(currency, context) {
      const er = this.exchangeRateList.find(
        (e) => e.month === context.month && e.code === currency
      )
      return er ? er.send_rate : { finalCost: 0 }
    },

    convertCosts(costs, thisMonth) {
      const month = thisMonth || this.curMonth
      return convertCosts(
        costs,
        this.displayCurrency,
        this.getExchangeRate.bind(this),
        { month },
        null
      )
    },

    setCostArray(data) {
      return data.reduce((arr, item) => {
        const convertCost = this.convertCosts(item.costs, this.curMonth)
        arr.push({
          from: item.from,
          to: item.to,
          usage: item.usage,
          cost: convertCost.cost,
          finalCost: convertCost.final_cost,
        })
        return arr
      }, [])
    },

    setTransferLineData(items) {
      const locs = {}
      const edges = []

      return items.data.reduce((obj, elem) => {
        const usage = formatBytes(elem.usage)

        locs[elem.from] = locs[elem.from] || elem.from
        locs[elem.to] = locs[elem.to] || elem.to

        if (elem.from === elem.to) {
          locs[elem.from] += `<br>&nbsp;&nbsp;&nbsp;<-> ${elem.to} : ${usage}`
        } else {
          locs[elem.from] += `<br>&nbsp;&nbsp;&nbsp;---> ${elem.to} : ${usage}`
          locs[elem.to] += `<br>&nbsp;&nbsp;&nbsp;<--- ${elem.from} : ${usage}`
        }

        edges.push({
          id: `${elem.from}-${elem.to}`,
          from: elem.from,
          to: elem.to,
          arrows: 'to',
          length: 300,
          label: usage,
          width: Math.floor(Math.log2(elem.usage) / 5),
          title: `${elem.from} ---> ${elem.to} : ${usage}`,
        })

        obj.locs = locs
        obj.edges = edges
        return obj
      }, {})
    },

    setTransferGraphData(data) {
      if (!data.length) {
        return []
      }
      const tabData = data.find((t) => t.name === this.activeTab)
      if (!tabData) {
        return []
      }
      const search = this.curProject ? this.curProject.id : '__total'
      const chartData = tabData.chart.find((c) => c.projectId === search)
      if (!chartData) {
        return []
      }
      const { locs, edges } = chartData.transferData
      return this.getChartData(locs, edges)
    },

    setTransferTableData(data) {
      if (!data.length) {
        return []
      }
      const tabData = data.find((t) => t.name === this.activeTab)
      if (!tabData) {
        return []
      }
      const search = this.curProject ? this.curProject.id : '__total'
      const tableData = tabData.table.find((c) => c.projectId === search)
      if (!tableData) {
        return []
      }
      return tableData.data
    },

    getChartData(locs, edges) {
      return {
        nodes: Object.keys(locs).map((loc) => ({
          id: loc,
          label: loc,
          title: locs[loc],
        })),
        edges,
        options: {
          interaction: { hover: true },
          layout: { randomSeed: Math.floor(new Date().getTime() / 1000 / 60) },
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.network-chart {
  height: 100%;
  border: 1px solid #dddddd;
}

// td 넓이 처리(하지않으면 정렬 틀어짐)
table td {
  width: 25%;
}
</style>
<style lang="scss" scoped>
@media only screen and (max-width: 800px) {
  .dataTransferDiv .md-table.md-theme-default.md-table-content,
  .dataTransferDiv .md-table.md-theme-default.md-table-alternate-header {
    max-height: none !important;
  }

  .dataTransferDiv .md-tabs-navigation .md-button {
    min-width: 185px !important;
  }

  .dataTransferDiv .md-tabs-navigation {
    overflow-x: auto !important;
  }
}
</style>
